import { btnUtils, makeToast, serializeArray, serializeForm } from "./utils";
import { isValidPostalCode } from "./zipCodeValidation";
import filterXSS from "xss";

/**
 * Sanitizes a string by filtering out all HTML tags and potentially harmful content via the imported xss package.
 * @param {string} str - The string to be sanitized.
 * @returns {string} The sanitized string.
 */
const sanitizeString = (str: string): string => {
  const xssOptions = {
    whiteList: {}, // empty, means filter out all tags
    stripIgnoreTag: true, // filter out all HTML not in the whitelist
    stripIgnoreTagBody: ["script"], // the script tag is a special case, we need to filter out its content
  };
  return filterXSS(str, xssOptions);
};

/**
 * Validates a form by checking required fields and applying custom validation rules. 
 * @param {HTMLFormElement | HTMLDivElement} form - The form element to be validated.
 * @param {string} [classIgnore] - An optional class name to ignore certain fields during validation.
 * @returns {boolean} Returns true if all validation requirements are met, otherwise false.
 */
export const formValidation = (form: HTMLFormElement | HTMLDivElement, classIgnore?: string) => {
  let allRequirementsMet = true,
    applyError = false,
    firstError = true,
    applyToast = "toast-input-validation";

  form.querySelectorAll(".rec-input-error").forEach((thisCurrentError) => {
    thisCurrentError.classList.remove("rec-input-error");
    thisCurrentError.removeAttribute("aria-invalid");
  });
  form.querySelectorAll(".rec-input-wrap-error").forEach((thisCurrentError) => {
    thisCurrentError.classList.remove("rec-input-wrap-error");
  });
  form
    .querySelectorAll(".rec-input-group-error")
    .forEach((thisCurrentError) => {
      thisCurrentError.classList.remove("rec-input-group-error");
    });
  document
    .querySelectorAll(".rec-slf-listing-error-address")
    .forEach((thisShippingError) => {
      thisShippingError.classList.add("rec-hidden");
    });
  const formValues =
    form.tagName == "FORM" ? serializeForm(form as HTMLFormElement) : serializeArray(form);

  /* function to get the input's value for the various types of inputs */

  form.querySelectorAll(".rec-required").forEach((element) => {
    const recquiredField = element as HTMLInputElement;
    let recquiredFieldValue = sanitizeString(formValues[recquiredField.name]);

    //ignore?
    if (classIgnore && recquiredField.classList.contains(classIgnore)) {
      // DO NOTHING
    } else {
      applyError = false;

      const fieldEmpty =
        recquiredField.type == "checkbox" &&
          recquiredField.classList.contains("check-required")
          ? !recquiredField.checked
          : recquiredFieldValue.length == 0 || recquiredFieldValue.trim() == "---";
      if (fieldEmpty) {
        applyError = true;

        //special case for listing address
        if (recquiredField.id.indexOf("shipping-address") >= 0) {
          document
            .querySelectorAll(".rec-slf-listing-error-address")
            .forEach((thisShippingError) => {
              thisShippingError.classList.remove("rec-hidden");
            });
          //special case for stars
        } else if (recquiredField.id == "rec-listing-rating-rating") {
          const ratingContainer = document.getElementById(
            "rec-listing-rating-container",
          );
          if (ratingContainer) {
            ratingContainer.classList.add("rec-input-group-error");
          } else {
            console.error(
              "Element with ID 'rec-listing-rating-container' not found.",
            );
          }
        }
      } else {
        //special case for zip code
        if (recquiredField.classList.contains("rec-required-zip")) {
          //special case for postal code
          let selectedCountryElement = form.querySelector(".rec-shipping-address-country") as HTMLSelectElement | null;
          let selectedCountry = selectedCountryElement ? selectedCountryElement.value : '';
          let selectedPostalCodeElement = form.querySelector(".rec-shipping-address-zip") as HTMLInputElement | null;
          let selectedPostalCode = selectedPostalCodeElement ? selectedPostalCodeElement.value.toUpperCase() : '';
          if (!isValidPostalCode(selectedCountry, selectedPostalCode)) {
            applyError = true;
            applyToast = "toast-input-validation-zip";
          }
        }
      }
      if (applyError) {
        allRequirementsMet = false;
        recquiredField.setAttribute("aria-invalid", "true");
        if (firstError) {
          firstError = false;
        }
        if (recquiredField.classList.contains("rec-input-group-control")) {
          const inputGroupElement = recquiredField.closest(".rec-input-group");
          if (inputGroupElement) {
            inputGroupElement.classList.add("rec-input-group-error");
          }
        }
        recquiredField.classList.add("rec-input-error");
        if (recquiredField.closest(".rec-slf-form-group")) {
          const formGroupElement = recquiredField.closest(".rec-slf-form-group");
          if (formGroupElement) {
            formGroupElement.classList.add("rec-input-wrap-error");
          }
        }
      }
    }
  });

  /* Now loop through any special validation fields (e.g. listing attributes) */
  form.querySelectorAll("[data-rec-validation]").forEach((element) => {
    const attributeValidation = element as HTMLInputElement;
    let attributeValidationValue = sanitizeString(formValues[attributeValidation.name]);
    applyError = false;

    try {
      if (
        attributeValidationValue.length > 0 &&
        (!classIgnore || !attributeValidation.classList.contains(classIgnore))
      ) {
        // TODO: generate a proper type for validation info. This would have to be reverse-engineered since the original implementation was JS.
        const validationInfo = JSON.parse(
          attributeValidation.getAttribute("data-rec-validation") || ""
        );

        //special min/max values property for numerics
        if (
          validationInfo &&
          "validation_min_value" in validationInfo &&
          parseFloat(attributeValidationValue) < validationInfo.validation_min_value
        ) {
          applyError = true;
        }
        if (
          validationInfo &&
          "validation_max_value" in validationInfo &&
          parseFloat(attributeValidationValue) > validationInfo.validation_max_value
        ) {
          applyError = true;
        }

        //special validation for select_multi and checkbox fields to make sure we have n number options selected
        if (
          validationInfo &&
          "validation_options_min" in validationInfo &&
          attributeValidationValue.split(",").length <
          validationInfo.validation_options_min
        ) {
          applyError = true;
        }
        if (
          validationInfo &&
          "validation_options_max" in validationInfo &&
          attributeValidationValue.split(",").length >
          validationInfo.validation_options_max
        ) {
          applyError = true;
        }

        if (validationInfo && "validation_custom_type" in validationInfo) {
          switch (validationInfo.validation_custom_type) {
            case "variant_weight":
              const variantWeightElem = document.getElementById(
                "variant-weight-grams",
              ) as HTMLInputElement | null;
              if (
                variantWeightElem &&
                variantWeightElem.value.toString().trim().length > 0
              ) {
                //Total weight in grams for this quantity
                const listingWeight =
                  parseFloat(variantWeightElem.value) *
                  parseFloat(attributeValidationValue);
                if (
                  listingWeight > validationInfo.validation_weight_max_grams
                ) {
                  applyError = true;
                }
              }
              break;
            case "price":
              attributeValidationValue = attributeValidationValue.replace(/[$]/g, "");
              if (isNaN(+attributeValidationValue) || attributeValidationValue === "") {
                applyError = true;
              } else {
                /* Confirm the value the seller entered meets our pricing criteria that we are getting from the settings file:
                                        - overall min price
                                        - overall max price
                                        - for matched listings, above the minimum percentage of the variant's MSRP
                                        - for matched listings, below the maximum percentage of the variant's MSRP
                                        */
                attributeValidationValue = parseFloat(attributeValidationValue).toFixed(2);
                attributeValidation.value = attributeValidationValue;
                const variantPriceElem =
                  form.querySelector("rec-variant-price") as HTMLInputElement | null;
                if (
                  validationInfo.validation_custom_min_price > 0 &&
                  attributeValidationValue < validationInfo.validation_custom_min_price
                ) {
                  applyError = true;
                } else if (
                  validationInfo.validation_custom_max_price > 0 &&
                  attributeValidationValue > validationInfo.validation_custom_max_price
                ) {
                  applyError = true;
                }
                if (variantPriceElem) {
                  const variantPrice = parseFloat(variantPriceElem.value);
                  const minPriceMSRP =
                    variantPrice *
                    (validationInfo.validation_custom_min_msrp_percentage /
                      100);
                  const maxPriceMSRP =
                    variantPrice *
                    (validationInfo.validation_custom_max_msrp_percentage /
                      100);
                  if (
                    validationInfo.validation_custom_min_msrp_percentage > 0 &&
                    Number(attributeValidationValue) < minPriceMSRP
                  ) {
                    applyError = true;
                  } else if (
                    validationInfo.validation_custom_max_msrp_percentage > 0 &&
                    Number(attributeValidationValue) > maxPriceMSRP
                  ) {
                    applyError = true;
                  }
                }
              }
              break;
            case "regex":
              const regex = new RegExp(validationInfo.validation_regex);
              applyError = !regex.test(attributeValidationValue);
              break;
          }
        }
      }
    } catch (err) {
      console.error("formValidation: ", err);
    }

    if (applyError) {
      allRequirementsMet = false;
      attributeValidation.setAttribute("aria-invalid", "true");
      if (firstError) {
        firstError = false;
      }
      if (attributeValidation.classList.contains("rec-input-group-control")) {
        const inputGroupElement = attributeValidation.closest(".rec-input-group");
        if (inputGroupElement) {
          inputGroupElement.classList.add("rec-input-group-error");
        }
      }
      attributeValidation.classList.add("rec-input-error");
      const formGroupElement = attributeValidation.closest(".rec-slf-form-group");
      if (formGroupElement) {
        formGroupElement.classList.add("rec-input-wrap-error");
      }
    }
  });

  if (!allRequirementsMet) {
    /* Apply focus on the first invalid input field (ADA) */
    const inputErrorElements = document.querySelectorAll(".rec-input-error");
    if (inputErrorElements.length > 0) {
      const firstInputErrorElement = inputErrorElements[0] as HTMLInputElement;
      firstInputErrorElement.focus();
    }
    btnUtils.reset();
    makeToast(applyToast);
  }

  return allRequirementsMet;
};
